import React, { useState, useRef, useLayoutEffect, useId } from 'react';
import './Fold.css';

/**
 * A section that shows its name and how much is inside it, and opens on a
 * click.
 *
 * The ticket detail page stacked every panel open: history, activity, files,
 * custom fields and a comment box, each in its own 2rem-padded card with 2rem
 * between them. A ticket with six history events, two comments and one file
 * came to 2085px against a 900px viewport - three screens to read one ticket.
 *
 * Two things make a fold honest rather than just shorter:
 *
 *   1. It says how much it is hiding. A fold labelled "Files" with nothing
 *      else on it is a fold nobody opens, so a ticket with an attachment on it
 *      reads as a ticket with none.
 *
 *   2. `mounted` keeps the children rendered while shut, hidden rather than
 *      unmounted. They fetch on mount, so the count is real as soon as the
 *      page settles - a count that only appears after you open the fold is no
 *      use for deciding whether to open it. It also means opening one is
 *      instant instead of starting a request.
 *
 * The count is read out of whatever the child panel already renders for it
 * (`.tl-count`, `.ta-count`, `.ttd-count`), because those components each count their own
 * rows and this one should not try to guess at their data. Pass `count`
 * directly when the parent already knows it.
 */
const Fold = ({ name, count, open = false, mounted = false, children }) => {
  const [isOpen, setIsOpen] = useState(open);
  const [seen, setSeen] = useState(null);
  const [empty, setEmpty] = useState(false);
  const bodyRef = useRef(null);
  const bodyId = useId();

  // Watch the child's own count, which arrives after its fetch resolves, and
  // whether it rendered anything at all.
  // Layout rather than passive, so an empty fold never paints a bar that
  // then disappears.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const read = () => {
      if (count === undefined) {
        const node = el.querySelector('.tl-count, .ta-count, .ttd-count');
        const n = node && String(node.textContent).match(/\d+/);
        setSeen(n ? Number(n[0]) : null);
      }
      // A category with no custom fields configured renders nothing at all, so
      // without this the ticket carried an "Extra fields" bar that opened onto
      // an empty box. A fold with nothing inside it is not a fold.
      setEmpty(el.children.length === 0 && !String(el.textContent || '').trim());
    };
    read();
    const obs = new MutationObserver(read);
    obs.observe(el, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [count, empty]);

  const shown = count !== undefined ? count : seen;
  if (mounted && empty) {
    // Still rendered, so the child keeps fetching and can bring the fold back
    // the moment it has something to show.
    return (
      <div hidden ref={bodyRef}>{children}</div>
    );
  }

  return (
    <section className="td-fold" data-open={String(isOpen)}>
      <button
        type="button"
        className="td-fold-head"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-controls={bodyId}
      >
        <span>{name}</span>
        {shown !== null && shown !== undefined && shown > 0 && (
          <span className="td-fold-count">{shown}</span>
        )}
        <span className="td-fold-chev" aria-hidden="true">▶</span>
      </button>
      {/* `hidden` rather than unmounting, so the panel inside has already
          fetched and can report its count while the fold is still shut. */}
      {(isOpen || mounted) && (
        <div className="td-fold-body" id={bodyId} ref={bodyRef} hidden={!isOpen}>
          {children}
        </div>
      )}
    </section>
  );
};

export default Fold;
